import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatTRY, formatDate, daysUntilLabel, daysUntil } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Fuel, CreditCard, Landmark, AlertTriangle, Loader2, TrendingUp } from "lucide-react";

const typeMeta = {
  fuel: { color: "#F59E0B", icon: Fuel, label: "Yakıt" },
  card: { color: "#3B82F6", icon: CreditCard, label: "Kart" },
  loan: { color: "#8B5CF6", icon: Landmark, label: "Kredi" },
};

function StatCard({ label, value, sub, color, icon: Icon, testid }) {
  return (
    <div className="card-surface p-5 fade-up" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-[#94A3B8]">{label}</span>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}1a` }}>
          <Icon className="w-[18px] h-[18px]" style={{ color }} />
        </div>
      </div>
      <div className="font-mono-num text-2xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-[#94A3B8] mt-1">{sub}</div>}
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#E65100]" />
      </div>
    );

  const chartData = data.outflow.map((o) => ({
    date: formatDate(o.date).slice(0, 5),
    tutar: o.amount,
  }));

  return (
    <div className="p-5 md:p-8 max-w-7xl">
      <PageHeader
        testid="overview-header"
        title={`Merhaba ${user?.name || ""} 👋`}
        subtitle={`${user?.station || ""} — bu ay ödenecek toplam tutar ve yaklaşan kritik ödemeler.`}
      />

      {/* Hero total */}
      <div className="card-surface p-6 md:p-7 mb-6 fade-up relative overflow-hidden" data-testid="hero-total-card">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#E65100]/10 blur-2xl" />
        <div className="relative">
          <div className="text-sm text-[#94A3B8]">Bu Ay Ödenecek Toplam</div>
          <div
            className="font-mono-num text-4xl md:text-5xl font-black tracking-tight mt-2"
            data-testid="stat-total-upcoming-amount"
          >
            {formatTRY(data.total_this_month)}
          </div>
          <div className="flex flex-wrap gap-4 mt-5 text-sm">
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Yakıt {formatTRY(data.fuel_this_month)}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#3B82F6]" /> Kart {formatTRY(data.card_this_month)}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#8B5CF6]" /> Kredi {formatTRY(data.loan_this_month)}
            </span>
          </div>
        </div>
      </div>

      {/* Overdue payments (red) */}
      {data.overdue_count > 0 && (
        <div className="card-surface p-5 md:p-6 mb-6 border border-[#EF4444]/40 bg-[#EF4444]/[0.06] fade-up" data-testid="overdue-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-[18px] h-[18px] text-[#EF4444]" />
            <h3 className="font-display text-lg font-semibold text-[#EF4444]">
              Geciken Ödemeler ({data.overdue_count})
            </h3>
            <span className="ml-auto font-mono-num text-lg font-bold text-[#EF4444]" data-testid="overdue-total">
              {formatTRY(data.overdue_total)}
            </span>
          </div>
          <div className="space-y-2">
            {data.overdue.map((e, i) => {
              const m = typeMeta[e.type];
              return (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#111620] border border-[#EF4444]/20" data-testid="overdue-item">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}1a` }}>
                    <m.icon className="w-4 h-4" style={{ color: m.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{e.title}</div>
                    <div className="text-xs text-[#EF4444]">{formatDate(e.date)} — Vadesi geçti</div>
                  </div>
                  <div className="font-mono-num text-sm font-semibold shrink-0 text-[#EF4444]">{formatTRY(e.amount)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard
          testid="stat-fuel-debt-amount" label="Toplam Yakıt Borcu" value={formatTRY(data.fuel_debt)}
          sub={`${data.counts.fuel} bekleyen fatura`} color="#F59E0B" icon={Fuel}
        />
        <StatCard
          testid="stat-cards-debt-amount" label="Kart Dönem Borcu" value={formatTRY(data.card_debt)}
          sub={`${data.counts.cards} kart`} color="#3B82F6" icon={CreditCard}
        />
        <StatCard
          testid="stat-loans-debt-amount" label="Kalan Kredi Borcu" value={formatTRY(data.loan_debt)}
          sub={`${data.counts.loans} aktif kredi`} color="#8B5CF6" icon={Landmark}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="card-surface p-6 lg:col-span-2 fade-up" data-testid="outflow-chart">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="w-[18px] h-[18px] text-[#E65100]" />
            <h3 className="font-display text-lg font-semibold">30 Günlük Nakit Çıkış Tahmini</h3>
          </div>
          {chartData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-[#94A3B8] text-sm">
              Önümüzdeki 30 günde planlanmış ödeme yok.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={chartData} margin={{ left: -10, right: 10 }}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E65100" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#E65100" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#253043" vertical={false} />
                <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis
                  stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{ background: "#161D2A", border: "1px solid #38475F", borderRadius: 12, color: "#F8FAFC" }}
                  formatter={(v) => [formatTRY(v), "Tutar"]}
                />
                <Area type="monotone" dataKey="tutar" stroke="#E65100" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Critical payments */}
        <div className="card-surface p-6 fade-up" data-testid="critical-panel">
          <div className="flex items-center gap-2 mb-5">
            <AlertTriangle className="w-[18px] h-[18px] text-[#EF4444]" />
            <h3 className="font-display text-lg font-semibold">Kritik Ödemeler (7 Gün)</h3>
          </div>
          {data.critical.length === 0 ? (
            <div className="text-sm text-[#94A3B8] py-8 text-center">
              Bu hafta vadesi gelen ödeme yok. 🎉
            </div>
          ) : (
            <div className="space-y-3">
              {data.critical.map((e, i) => {
                const m = typeMeta[e.type];
                const dleft = daysUntil(e.date);
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#111620]" data-testid="critical-item">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}1a` }}>
                      <m.icon className="w-4 h-4" style={{ color: m.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{e.title}</div>
                      <div className="text-xs text-[#94A3B8]">{formatDate(e.date)}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-num text-sm font-semibold">{formatTRY(e.amount)}</div>
                      <div className={`text-[11px] ${dleft <= 1 ? "text-[#EF4444]" : "text-[#F59E0B]"}`}>
                        {daysUntilLabel(e.date)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-6">
        <button onClick={() => navigate("/yakit")} data-testid="btn-add-fuel-invoice" className="px-4 py-2.5 rounded-xl bg-[#161D2A] hover:bg-[#1D2637] border border-[#253043] text-sm font-medium transition-colors flex items-center gap-2">
          <Fuel className="w-4 h-4 text-[#F59E0B]" /> Yeni Yakıt Alımı
        </button>
        <button onClick={() => navigate("/kartlar")} data-testid="btn-add-credit-card" className="px-4 py-2.5 rounded-xl bg-[#161D2A] hover:bg-[#1D2637] border border-[#253043] text-sm font-medium transition-colors flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-[#3B82F6]" /> Kart Ekle
        </button>
        <button onClick={() => navigate("/krediler")} data-testid="btn-add-loan" className="px-4 py-2.5 rounded-xl bg-[#161D2A] hover:bg-[#1D2637] border border-[#253043] text-sm font-medium transition-colors flex items-center gap-2">
          <Landmark className="w-4 h-4 text-[#8B5CF6]" /> Kredi Ekle
        </button>
      </div>
    </div>
  );
}
